import { z } from "zod";
import { entitySchemas, type EntityType } from "./entities";
import { invoiceDeletionBlocker, isEntryLocked } from "./invoices";
import { subTasks } from "./tasks";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";

export const TRASH_DAYS = 30;
const DAY = 86_400_000;

export interface TrashItem {
  type: Exclude<EntityType, "settings">;
  id: string;
  label: string;
  deletedAt: number;
  expiresAt: number;
}

/**
 * Whether an entity can go to Trash. Clients, Projects, and Tasks that still have time logged
 * against them can only be Archived, so no entry is ever left pointing at nothing.
 */
export function deletionBlocker(dataset: Dataset, type: Exclude<EntityType, "settings">, id: string): string | null {
  const entries = Object.values(dataset.timeEntries).filter(isLive);
  if (type === "task") {
    const family = new Set([id, ...subTasks(dataset, id).map((task) => task.id)]);
    if (entries.some((entry) => entry.taskId === id)) return "This task has time logged on it. Archive it instead.";
    if (entries.some((entry) => entry.taskId !== null && family.has(entry.taskId))) return "This task's sub-tasks have time logged on them. Archive it instead.";
  }
  if (type === "project" && entries.some((entry) => entry.projectId === id)) return "This project has time logged on it. Archive it instead.";
  if (type === "client") {
    const projects = Object.values(dataset.projects).filter((project) => isLive(project) && project.clientId === id);
    if (projects.length) return "This client still has projects. Archive it instead, or move its projects first.";
    if (Object.values(dataset.invoices).some((invoice) => isLive(invoice) && invoice.clientId === id)) return "This client has invoices. Archive it instead.";
  }
  const entry = type === "timeEntry" ? dataset.timeEntries[id] : undefined;
  if (entry && isEntryLocked(dataset, entry)) return "This entry is on a sent invoice.";
  const invoice = type === "invoice" ? dataset.invoices[id] : undefined;
  if (invoice) return invoiceDeletionBlocker(dataset, invoice);
  return null;
}

/** Items in Trash, newest first. Items older than the retention window are excluded. */
export function trashItems(dataset: Dataset, now: number): TrashItem[] {
  const items: TrashItem[] = [];
  const push = (type: TrashItem["type"], id: string, label: string, deletedAt: number | null, discardedAt: number | null) => {
    if (deletedAt === null || discardedAt !== null) return;
    const expiresAt = deletedAt + TRASH_DAYS * DAY;
    if (expiresAt > now) items.push({ type, id, label, deletedAt, expiresAt });
  };
  for (const client of Object.values(dataset.clients)) push("client", client.id, client.name, client.deletedAt, client.discardedAt);
  for (const project of Object.values(dataset.projects)) push("project", project.id, project.name, project.deletedAt, project.discardedAt);
  for (const task of Object.values(dataset.tasks)) {
    const parent = task.parentId ? dataset.tasks[task.parentId] : undefined;
    // Sub-tasks trashed with their parent are listed, restored, and discarded as part of it.
    if (parent && parent.deletedAt !== null && parent.deletedAt === task.deletedAt) continue;
    push("task", task.id, parent ? `${parent.name} › ${task.name}` : task.name, task.deletedAt, task.discardedAt);
  }
  for (const entry of Object.values(dataset.timeEntries)) push("timeEntry", entry.id, entry.description || "Time entry", entry.deletedAt, entry.discardedAt);
  for (const event of Object.values(dataset.events)) if (event.seriesId === null) push("event", event.id, event.title || "Untitled event", event.deletedAt, event.discardedAt);
  for (const invoice of Object.values(dataset.invoices)) push("invoice", invoice.id, `Draft invoice for ${dataset.clients[invoice.clientId]?.name ?? "a client"}`, invoice.deletedAt, invoice.discardedAt);
  return items.sort((a, b) => b.deletedAt - a.deletedAt);
}

/**
 * Restores or discards a Trash item. A Task brings the sub-tasks trashed with it; a sub-task
 * restored on its own brings its parent back too, so it never sits under a Task in Trash.
 */
export function trashItemOperations(dataset: Dataset, factory: OperationFactory, item: Pick<TrashItem, "type" | "id">, action: "restore" | "discard", now: number): SyncOperation[] {
  const patch = action === "restore" ? { deletedAt: null } : { discardedAt: now };
  const operations = [factory.op(item.type, item.id, patch)];
  const task = item.type === "task" ? dataset.tasks[item.id] : undefined;
  if (!task || task.deletedAt === null) return operations;
  for (const child of Object.values(dataset.tasks)) {
    if (child.parentId === task.id && child.discardedAt === null && child.deletedAt === task.deletedAt) operations.push(factory.op("task", child.id, patch));
  }
  const parent = task.parentId ? dataset.tasks[task.parentId] : undefined;
  if (action === "restore" && parent && parent.deletedAt !== null && parent.discardedAt === null) operations.push(factory.op("task", parent.id, { deletedAt: null }));
  return operations;
}

/** Permanently discards Trash items past the retention window. */
export function expiredTrashOperations(dataset: Dataset, factory: OperationFactory, now: number): SyncOperation[] {
  const operations: SyncOperation[] = [];
  type Trashable = { id: string; deletedAt: number | null; discardedAt: number | null };
  const collections: Array<[TrashItem["type"], Record<string, Trashable>]> = [["client", dataset.clients], ["project", dataset.projects], ["task", dataset.tasks], ["timeEntry", dataset.timeEntries], ["invoice", dataset.invoices], ["event", dataset.events]];
  for (const [type, records] of collections) {
    for (const entity of Object.values(records)) {
      if (entity.deletedAt !== null && entity.discardedAt === null && entity.deletedAt + TRASH_DAYS * DAY <= now) {
        operations.push(factory.op(type, entity.id, { discardedAt: now }));
      }
    }
  }
  return operations;
}

/** Full JSON Backup. Restoring writes every entity back through ordinary operations. */
export const backupSchema = z.object({
  format: z.literal("otrack-backup"),
  version: z.literal(1),
  exportedAt: z.number().int().nonnegative(),
  settings: entitySchemas.settings,
  clients: z.array(entitySchemas.client),
  projects: z.array(entitySchemas.project),
  tasks: z.array(entitySchemas.task),
  timeEntries: z.array(entitySchemas.timeEntry),
  /** Missing from backups made before invoicing existed. */
  invoices: z.array(entitySchemas.invoice).default([]),
  /** Missing from backups made before the Calendar existed. */
  events: z.array(entitySchemas.event).default([])
});
export type Backup = z.infer<typeof backupSchema>;

export function createBackup(dataset: Dataset, now: number): Backup {
  const keep = <T extends { discardedAt: number | null }>(records: Record<string, T>) => Object.values(records).filter((entity) => entity.discardedAt === null);
  return {
    format: "otrack-backup",
    version: 1,
    exportedAt: now,
    settings: dataset.settings,
    clients: keep(dataset.clients),
    projects: keep(dataset.projects),
    tasks: keep(dataset.tasks),
    timeEntries: keep(dataset.timeEntries),
    invoices: keep(dataset.invoices),
    events: keep(dataset.events)
  };
}

const internalFields = new Set(["id", "createdAt", "updatedAt", "fieldClocks"]);

function fieldsOf(entity: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(entity).filter(([field]) => !internalFields.has(field)));
}

/** Operations that write a Backup's entities over the current Dataset (same ids are replaced). */
export function restoreOperations(backup: Backup, factory: OperationFactory): SyncOperation[] {
  return [
    factory.op("settings", backup.settings.id, fieldsOf(backup.settings)),
    ...backup.clients.map((entity) => factory.op("client", entity.id, fieldsOf(entity))),
    ...backup.projects.map((entity) => factory.op("project", entity.id, fieldsOf(entity))),
    ...backup.tasks.map((entity) => factory.op("task", entity.id, fieldsOf(entity))),
    ...backup.timeEntries.map((entity) => factory.op("timeEntry", entity.id, fieldsOf(entity))),
    ...backup.invoices.map((entity) => factory.op("invoice", entity.id, fieldsOf(entity))),
    ...backup.events.map((entity) => factory.op("event", entity.id, fieldsOf(entity)))
  ];
}
